import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Smartphone, Check, Plus, X, RefreshCw, MessageSquare } from 'lucide-react';
import {
  buildPaymentReminderMessage,
  emptySmsReminderSettings,
  readSmsReminderSettings,
  writeSmsReminderSettings,
  type SmsReminderSettings,
} from '../../lib/smsReminders';
import { companyDisplayName, readCompanyProfile } from '../../lib/companyProfile';
import { smsApi, ApiError, type SmsUsageReport, type SmsUsageSort } from '../../api';
import { toast } from 'react-toastify';
import { useTranslation } from '../../lib/i18n';
import { planAllowsItem } from '../../lib/subscription';
import { SMS_REMINDER_FEATURE_ID } from '../../lib/planEntitlements';

function toYmdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysAgoYmd(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toYmdLocal(d);
}

const SmsReminderSettingsPanel: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<SmsReminderSettings>(() => readSmsReminderSettings());
  const [newDay, setNewDay] = useState('1');
  const [saved, setSaved] = useState(false);
  const [running, setRunning] = useState(false);
  const [apiStatus, setApiStatus] = useState<{ configured: boolean; templatePreview?: string } | null>(null);
  const [usage, setUsage] = useState<SmsUsageReport | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageFrom, setUsageFrom] = useState(() => daysAgoYmd(29));
  const [usageTo, setUsageTo] = useState(() => toYmdLocal(new Date()));
  const [usageSort, setUsageSort] = useState<SmsUsageSort>('date_desc');

  const loadUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      setUsage(
        await smsApi.usage({
          from: usageFrom || undefined,
          to: usageTo || undefined,
          sort: usageSort,
          limit: 50,
        })
      );
    } catch {
      setUsage(null);
    } finally {
      setUsageLoading(false);
    }
  }, [usageFrom, usageTo, usageSort]);

  useEffect(() => {
    smsApi
      .status()
      .then(setApiStatus)
      .catch(() => setApiStatus({ configured: false }));
  }, []);

  useEffect(() => {
    void loadUsage();
  }, [loadUsage]);

  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [saved]);

  const businessName = companyDisplayName(readCompanyProfile()) || 'Your business';
  const smsAllowed = planAllowsItem(SMS_REMINDER_FEATURE_ID);

  const preview = useMemo(
    () =>
      apiStatus?.templatePreview ||
      buildPaymentReminderMessage({
        customerName: 'Mahi',
        financeName: businessName,
        amount: 4500,
        dueDate: new Date(Date.now() + 1 * 86400000).toISOString().slice(0, 10),
        haNumber: 'INV1025',
      }),
    [apiStatus?.templatePreview, businessName]
  );

  const addDay = () => {
    const d = Math.max(1, Math.min(60, parseInt(newDay, 10) || 0));
    if (!d) return;
    if (form.reminderDaysBefore.includes(d)) {
      toast.info(`${d} days is already in the list.`);
      return;
    }
    setForm({ ...form, reminderDaysBefore: [...form.reminderDaysBefore, d].sort((a, b) => b - a) });
    setNewDay('');
  };

  const removeDay = (day: number) => {
    setForm({ ...form, reminderDaysBefore: form.reminderDaysBefore.filter((d) => d !== day) });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!smsAllowed) {
      toast.error('Your subscription plan does not include SMS payment reminders.');
      return;
    }
    if (form.enabled && form.reminderDaysBefore.length === 0) {
      toast.error('Add at least one reminder day (e.g. 7, 4, 2) before enabling.');
      return;
    }
    writeSmsReminderSettings(form);
    setSaved(true);
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await smsApi.runReminders();
      toast.success(`SMS reminders sent: ${res.sent}. Skipped: ${res.skipped}.`);
      void loadUsage();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not run reminders.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      {!smsAllowed ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          SMS reminders are not enabled on this shop&apos;s subscription plan. Platform super admin must open{' '}
          <strong>Plans</strong>, edit this plan, turn on <strong>SMS payment reminders</strong>, and save. After
          that, refresh or re-login here to enable sending. Usage totals below still show any SMS already sent.
        </div>
      ) : null}

      <div className="flex items-center gap-2 text-zinc-900">
        <Smartphone className="h-5 w-5 text-emerald-700" aria-hidden />
        <p className="text-sm text-zinc-600">
          {t('settings.smsDesc')} SMS is sent one (or more) days before the EMI due date — set the days below.
          Example: loan registered today with EMI due on the 25th and &quot;1 day before&quot; enabled → SMS goes on the 24th.
        </p>
      </div>

      {/* Usage meter — date-range billing tracker */}
      <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-emerald-700" />
            <div>
              <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">SMS usage</p>
              <p className="text-[11px] text-zinc-500">
                Tracked for billing · ₹{(usage?.unitCostInr ?? 0.25).toFixed(2)} per successful SMS
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void loadUsage()}
            disabled={usageLoading}
            className="app-btn-secondary inline-flex items-center gap-1.5 text-xs"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${usageLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-zinc-100 bg-zinc-50/80 p-3 dark:border-zinc-700 dark:bg-zinc-800/50">
          <label className="text-xs">
            <span className="font-semibold uppercase tracking-wide text-zinc-500">From</span>
            <input
              type="date"
              value={usageFrom}
              onChange={(e) => setUsageFrom(e.target.value)}
              className="mt-1 block rounded-lg border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
            />
          </label>
          <label className="text-xs">
            <span className="font-semibold uppercase tracking-wide text-zinc-500">To</span>
            <input
              type="date"
              value={usageTo}
              onChange={(e) => setUsageTo(e.target.value)}
              className="mt-1 block rounded-lg border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
            />
          </label>
          <label className="text-xs">
            <span className="font-semibold uppercase tracking-wide text-zinc-500">Sort daily</span>
            <select
              value={usageSort}
              onChange={(e) => setUsageSort(e.target.value as SmsUsageSort)}
              className="mt-1 block rounded-lg border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
            >
              <option value="date_desc">Date ↓ newest</option>
              <option value="date_asc">Date ↑ oldest</option>
              <option value="count_desc">Count ↓ high</option>
              <option value="count_asc">Count ↑ low</option>
            </select>
          </label>
          <div className="flex flex-wrap gap-1.5 pb-0.5">
            {[
              { label: '7d', from: daysAgoYmd(6), to: toYmdLocal(new Date()) },
              { label: '30d', from: daysAgoYmd(29), to: toYmdLocal(new Date()) },
              {
                label: 'This month',
                from: toYmdLocal(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
                to: toYmdLocal(new Date()),
              },
              { label: 'All time', from: '', to: '' },
            ].map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => {
                  setUsageFrom(p.from);
                  setUsageTo(p.to);
                }}
                className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-100 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'In selected range', value: usage?.periodSent ?? usage?.totalSent ?? '—' },
            {
              label: 'Range cost',
              value: usage ? `₹${(usage.periodCostInr ?? usage.estimatedCostInr).toFixed(2)}` : '—',
            },
            { label: 'Last 7 days', value: usage?.last7d ?? '—' },
            { label: 'Last 30 days', value: usage?.last30d ?? '—' },
          ].map((card) => (
            <div key={card.label} className="rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800/80">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{card.label}</p>
              <p className="mt-0.5 text-lg font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">{card.value}</p>
            </div>
          ))}
        </div>

        {usage ? (
          <p className="mt-2 text-xs text-zinc-500">
            {usage.period?.filtered
              ? `Range ${usage.period.fromYmd || '…'} → ${usage.period.toYmd || '…'} · `
              : 'All time · '}
            Triggers — manual: {usage.byTrigger.manual} · schedule: {usage.byTrigger.schedule} · cron: {usage.byTrigger.cron}
            {usage.failed ? ` · Failed: ${usage.failed}` : ''}
            {usage.lastSentAt ? ` · Last in range ${new Date(usage.lastSentAt).toLocaleString('en-IN')}` : ''}
          </p>
        ) : null}

        {usage?.daily?.length ? (
          <div className="mt-4 overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
            <p className="border-b border-zinc-100 bg-zinc-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800">
              Daily breakdown ({usage.daily.length} day{usage.daily.length === 1 ? '' : 's'})
            </p>
            <table className="app-table min-w-full text-xs">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="text-right">SMS sent</th>
                  <th className="text-right">Est. cost</th>
                </tr>
              </thead>
              <tbody>
                {usage.daily.map((row) => (
                  <tr key={row.date}>
                    <td className="tabular-nums">{row.date}</td>
                    <td className="text-right font-semibold tabular-nums">{row.count}</td>
                    <td className="text-right tabular-nums">₹{row.costInr.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {usage?.recent?.length ? (
          <div className="mt-4 overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
            <p className="border-b border-zinc-100 bg-zinc-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800">
              Recent sends in range
            </p>
            <table className="app-table min-w-full text-xs">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Status</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Amount</th>
                  <th>Due</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {usage.recent.slice(0, 40).map((row) => (
                  <tr key={row.id}>
                    <td className="whitespace-nowrap tabular-nums">
                      {row.at ? new Date(row.at).toLocaleString('en-IN') : '—'}
                    </td>
                    <td>
                      <span
                        className={
                          row.status === 'sent'
                            ? 'font-semibold text-emerald-700'
                            : 'font-semibold text-rose-700'
                        }
                      >
                        {row.status === 'sent' ? 'Sent' : 'Failed'}
                      </span>
                    </td>
                    <td>{row.customerName || '—'}</td>
                    <td className="tabular-nums">{row.phone || '—'}</td>
                    <td className="tabular-nums">{row.amount ? `₹${row.amount}` : '—'}</td>
                    <td>{row.dueDate || '—'}</td>
                    <td className="max-w-[280px] truncate text-zinc-600" title={buildPaymentReminderMessage({
                      customerName: row.customerName || 'Customer',
                      amount: row.amount || 0,
                      dueDate: row.dueDate || '',
                      refId: row.refId,
                    })}>
                      {buildPaymentReminderMessage({
                        customerName: row.customerName || 'Customer',
                        amount: row.amount || 0,
                        dueDate: row.dueDate || '',
                        refId: row.refId,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-3 text-xs text-zinc-500">No SMS in this date range. Try another range or send a reminder.</p>
        )}
      </div>

      {apiStatus && !apiStatus.configured ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          SMS sending is not available right now. You can still save reminder days. Contact support if this continues.
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className={`space-y-5 ${smsAllowed ? '' : 'pointer-events-none opacity-60'}`}>
        <label className="flex items-center gap-2 text-sm font-medium text-zinc-800">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            disabled={!smsAllowed}
            className="rounded border-zinc-300"
          />
          {t('settings.sms')}
        </label>

        <div>
          <p className="app-label">{t('settingsForm.reminderDays')}</p>
          <p className="mb-2 text-xs text-zinc-500">
            Add each schedule you want — e.g. <strong>1 day before</strong> (recommended), or also 7 / 3 / 2 days before.
          </p>
          <div className="flex flex-wrap gap-2">
            {form.reminderDaysBefore.map((day) => (
              <span
                key={day}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-900"
              >
                {day} day{day === 1 ? '' : 's'} before
                <button
                  type="button"
                  onClick={() => removeDay(day)}
                  className="rounded-full p-0.5 hover:bg-emerald-100"
                  aria-label={`Remove ${day} days`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
            {form.reminderDaysBefore.length === 0 ? (
              <span className="text-sm text-zinc-500">No days added — use the field below.</span>
            ) : null}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <input
              type="number"
              min={1}
              max={60}
              className="app-field w-28"
              value={newDay}
              onChange={(e) => setNewDay(e.target.value)}
              placeholder="Days"
            />
            <button type="button" onClick={addDay} className="app-btn-secondary gap-1 text-sm">
              <Plus className="h-4 w-4" />
              {t('common.add')}
            </button>
            <button
              type="button"
              onClick={() => setForm(emptySmsReminderSettings())}
              className="app-btn-secondary text-sm"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/60">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Sample SMS text</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-zinc-800 dark:text-zinc-200">{preview}</pre>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-zinc-100 pt-4">
          {saved && (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700">
              <Check className="h-4 w-4" /> {t('common.saved')}
            </span>
          )}
          {apiStatus?.configured && form.enabled ? (
            <button type="button" onClick={() => void runNow()} disabled={running} className="app-btn-secondary">
              {running ? t('common.loading') : 'Run reminders now'}
            </button>
          ) : null}
          <button type="submit" className="app-btn-primary">
            {t('settingsForm.saveReminders')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SmsReminderSettingsPanel;
